import { useCallback, useEffect, useMemo, useRef, useState, Dispatch, SetStateAction, ComponentProps, type JSX } from 'react';
import { useIntl } from 'react-intl';
import logPhotoOverflowClicked from '@alltrails/amplitude/events/logPhotoOverflowClicked';
import ReportLocation from '@alltrails/amplitude/enums/ReportLocation';
import type Context from '@alltrails/core/types/Context';
import { FullscreenModal } from '@alltrails/modal';
import type Trail from '@alltrails/core/types/Trail';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import compareUserIds from '@alltrails/shared/utils/compareUserIds';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import {
  LightboxItem,
  LightboxPhoto,
  ActionPhotoLink,
  LightboxDropdownHandlers,
  SuccessToast,
  PhotoType,
  LightboxUser,
  DropdownMenuOption,
  LinkInfo,
  LightboxDropdownMenuState
} from '@alltrails/denali/types';
import CheckOutline from '@alltrails/denali/icons/CheckOutline';
import Error from '@alltrails/denali/icons/Error';
import ArrowSq from '@alltrails/denali/icons/ArrowSq';
import Check from '@alltrails/denali/icons/Check';
import LightboxHeader from '@alltrails/denali/components/LightboxHeader';
import DenaliLightboxPhoto from '@alltrails/denali/components/LightboxPhoto';
import IconButton from '@alltrails/denali/components/IconButton';
import LightboxFooter from '@alltrails/denali/components/LightboxFooter';
import Toast from '@alltrails/denali/components/Toast';
import useReportingToast from '@alltrails/reporting/hooks/useReportingToast';
import BlockUserMenuOption from '@alltrails/reporting/components/BlockUserMenuOption';
import ReportIssueModal from '@alltrails/reporting/components/ReportIssueModal';
import { EntryPoint } from '@alltrails/reporting/types';
import useLightboxState from '../../hooks/useLightboxState';
import useCarouselState from '../../hooks/useCarouselState';
import { deletePhoto, removePhotoFromMap, removePhotoFromTrail, renamePhoto, suggestDeletePhotoFromTrail, unhidePhoto } from '../../utils/requests';
import {
  generateDropdownOptions,
  photoAlt,
  photoUser,
  closeDropdownAndHandlePromise,
  photoItemId,
  setAsHighlightRequests
} from '../../utils/dropdownOptions';
import RenamePhotoModal from '../RenamePhotoModal/RenamePhotoModal';
import DeletePhotoAlertDialog from '../DeletePhotoAlertDialog/DeletePhotoAlertDialog';
import styles from './styles/styles.module.scss';

type LightboxProps = {
  context?: Context;
  images: LightboxItem[];
  startingIndex: number;
  onNavigate?: (newActiveIndex: number) => Promise<LightboxPhoto>;
  onPhotoViewed?: (photo: LightboxPhoto['photo']) => void;
  onUserClick?: (user: LightboxUser) => void;
  languageRegionCode: LanguageRegionCode;
  onClose: () => void;
  linkedReview?: { id: number };
  linkedTrail?: Pick<Trail, 'id' | 'name' | 'slug'>;
  count?: number;
  setCount?: Dispatch<SetStateAction<number>>;
  analyticsReportLocation?: ReportLocation;
  shouldHideActions?: boolean;
  photosPageLinkInfo?: LinkInfo;
  onPhotosPageLinkClick?: () => void;
  showTimestampOnly?: boolean;
} & Pick<ComponentProps<typeof FullscreenModal>, 'hidePageScrollbarGutter'>;

const LightboxModal = ({
  context,
  images,
  startingIndex,
  onNavigate,
  onPhotoViewed,
  onUserClick,
  languageRegionCode,
  onClose,
  linkedReview,
  linkedTrail,
  count,
  setCount,
  analyticsReportLocation,
  shouldHideActions,
  photosPageLinkInfo,
  onPhotosPageLinkClick,
  hidePageScrollbarGutter,
  showTimestampOnly
}: LightboxProps): JSX.Element => {
  const intl = useIntl();
  const lightboxNavigationButtonRef = useRef<HTMLButtonElement>(null);
  const { currentPhoto, handleLoadPhoto } = useLightboxState(images, onNavigate);
  const { activeIndex, navigateNext, navigatePrev, isLoading } = useCarouselState(images.length, startingIndex, handleLoadPhoto, count, setCount);
  const isOwner = currentPhoto?.photo && compareUserIds(context?.currentUser?.id, currentPhoto?.photo?.user_id);
  const [dropdownMenuState, setDropdownMenuState] = useState<LightboxDropdownMenuState>({
    isOpen: false,
    options: []
  });
  const [isRenamePhotoModalOpen, setIsRenamePhotoModalOpen] = useState(false);
  const [isDeletePhotoModalOpen, setIsDeletePhotoModalOpen] = useState(false);
  const [updatedPhotoTitle, setUpdatedPhotoTitle] = useState<string>('');
  const [errorToastVisible, setIsErrorToastVisible] = useState<boolean>(false);
  const [successToast, setSuccessToast] = useState<SuccessToast>({ isVisible: false, text: '' });
  const [isReportingModalOpen, setIsReportingModalOpen] = useState<boolean>(false);
  const [reportingSuccessToast, setReportingSuccessToast] = useReportingToast();
  const { hasPermission } = useAuthorization();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        navigatePrev();
      } else if (e.key === 'ArrowRight') {
        navigateNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('beforeunload', onClose);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('beforeunload', onClose);
    };
  }, [navigateNext, navigatePrev, onClose]);

  useEffect(() => {
    if (errorToastVisible) {
      setTimeout(() => {
        setIsErrorToastVisible(false);
      }, 3000);
    }

    if (successToast && successToast.isVisible) {
      setTimeout(() => {
        setSuccessToast({ isVisible: false, text: '' });
      }, 3000);
    }
    // modal should maintain focus to allow for immediate key navigation
    lightboxNavigationButtonRef.current?.focus();
  }, [errorToastVisible, successToast, navigateNext, navigatePrev]);

  useEffect(() => {
    if (currentPhoto) {
      setUpdatedPhotoTitle(currentPhoto.creditTitle?.title || '');
    }
  }, [currentPhoto]);

  useEffect(() => {
    if (currentPhoto) {
      onPhotoViewed?.(currentPhoto.photo);
    }
  }, [currentPhoto, onPhotoViewed]);

  const dropdownHandlers: LightboxDropdownHandlers = {
    onDeletePhotoClick: () => setIsDeletePhotoModalOpen(true),
    onRemovePhotoClick: (type: PhotoType) => {
      if (currentPhoto?.photo) {
        const removeId = photoItemId(type, currentPhoto);
        const promise = type === 'map' ? removePhotoFromMap(removeId, currentPhoto.photo.id) : removePhotoFromTrail(removeId, currentPhoto.photo.id);
        closeDropdownAndHandlePromise(
          promise,
          'remove photo',
          intl.formatMessage({ defaultMessage: 'Photo removed' }),
          setSuccessToast,
          setIsErrorToastVisible
        );
      }
      return null;
    },
    onSuggestHideClick: () => {
      if (currentPhoto && currentPhoto?.photo?.trail_id) {
        const promise = suggestDeletePhotoFromTrail(currentPhoto?.photo?.trail_id, currentPhoto.photo.id);
        closeDropdownAndHandlePromise(
          promise,
          'suggest hide',
          intl.formatMessage({ defaultMessage: 'Suggestion sent' }),
          setSuccessToast,
          setIsErrorToastVisible
        );
      }
    },
    onSetAsHighlightClick: (type: PhotoType) => {
      if (currentPhoto?.photo) {
        const itemId = photoItemId(type, currentPhoto);
        const promise = setAsHighlightRequests(type, itemId, currentPhoto.photo.id);
        closeDropdownAndHandlePromise(
          promise,
          'set as highlight',
          intl.formatMessage({ defaultMessage: 'Highlight photo updated' }),
          setSuccessToast,
          setIsErrorToastVisible
        );
      }
    }
  };
  const onPhotoUnhideClick = async () => {
    if (currentPhoto?.photo) {
      try {
        await unhidePhoto(currentPhoto.photo.id);
        setSuccessToast({
          isVisible: true,
          text: intl.formatMessage({ defaultMessage: 'Photo unhidden' })
        });
      } catch (e) {
        console.error('Error occurred while attempting to unhide the lightbox photo.', e);
        setIsErrorToastVisible(true);
      }
    }
  };

  const getCurrentPhotoDropdownMenuOptions = (currentPhotoLink: ActionPhotoLink) => generateDropdownOptions(currentPhotoLink, dropdownHandlers);
  const dropdownOptions: DropdownMenuOption[] = useMemo(() => {
    if (!context || !context.currentUser || !currentPhoto) {
      return [];
    }
    const { firstLink, secondLink, thirdLink, photoUnhideLink } = currentPhoto;
    const options: DropdownMenuOption[] = [];
    if (context.currentUser.id !== currentPhoto?.photo?.user_id) {
      options.push({
        title: intl.formatMessage({ defaultMessage: 'Report an issue' }),
        onClick: () => setIsReportingModalOpen(true),
        testId: 'photo-report-an-issue-lightbox-dropdown-option'
      });
    }
    if (context.currentUser.id !== currentPhoto?.photo?.user_id && currentPhoto?.creditTitle?.user_name) {
      const user = photoUser(currentPhoto);
      if (user) {
        options.push(
          <BlockUserMenuOption
            currentUserId={context.currentUser.id}
            blockUserId={user.id}
            setSuccessToast={setReportingSuccessToast}
            className={styles.blockMenuItem}
            customTitle
            isInsideModalInterior
            blockCallback={() => window.location.reload()}
          />
        );
      }
    }
    const renameAction: DropdownMenuOption = {
      title: intl.formatMessage({ defaultMessage: 'Rename' }),
      onClick: () => setIsRenamePhotoModalOpen(true),
      testId: 'rename-lightbox-dropdown-option'
    };

    const hasAdminLinks =
      hasPermission({ permission: 'trails:manage' }) && ((photoUnhideLink && onPhotoUnhideClick) || firstLink || secondLink || thirdLink);

    if (hasAdminLinks) {
      options.push({
        title: intl.formatMessage({ defaultMessage: 'Admin' }),
        className: styles.adminItem,
        testId: 'photo-admin-lightbox-dropdown-option'
      });
    }
    if (photoUnhideLink && hasPermission({ permission: 'trails:manage' }) && onPhotoUnhideClick) {
      options.push({
        title: photoUnhideLink.label,
        onClick: onPhotoUnhideClick,
        testId: 'photo-unhide-lightbox-dropdown-option'
      });
    }
    if (firstLink) {
      options.push(getCurrentPhotoDropdownMenuOptions(firstLink)[0]);
    }
    if (secondLink) {
      options.push(getCurrentPhotoDropdownMenuOptions(secondLink)[0]);
    }
    if (thirdLink) {
      options.push(getCurrentPhotoDropdownMenuOptions(thirdLink)[0]);
    }
    if (isOwner) {
      options.unshift(renameAction);
    }

    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, currentPhoto, isOwner]);

  const onRenamePhotoChange = useCallback((title: string) => setUpdatedPhotoTitle(title), []);
  const onRenamePhotoSave = async () => {
    if (currentPhoto?.photo) {
      try {
        await renamePhoto(currentPhoto.photo.id, updatedPhotoTitle);
        setIsRenamePhotoModalOpen(false);
        setSuccessToast({ isVisible: true, text: intl.formatMessage({ defaultMessage: 'Photo renamed' }) });
      } catch (e) {
        console.error('Error occured while renaming the lightbox photo', e);
        setIsRenamePhotoModalOpen(false);
        setIsErrorToastVisible(true);
      }
    }
  };

  const handleDeletePhoto = async () => {
    if (currentPhoto?.photo) {
      try {
        await deletePhoto(currentPhoto.photo.id);
        setIsDeletePhotoModalOpen(false);
        setSuccessToast({ isVisible: true, text: intl.formatMessage({ defaultMessage: 'Photo deleted' }) });
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } catch (e) {
        console.error('Error occured while deleting the lightbox photo', e);
        setIsDeletePhotoModalOpen(false);
        setIsErrorToastVisible(true);
      }
    }
  };
  const onDropdownMenuClick = () => {
    if (currentPhoto?.photo) {
      logPhotoOverflowClicked({
        photo_id: currentPhoto.photo.id.toString(),
        ...(linkedReview ? { review_id: linkedReview.id.toString() } : {}),
        ...(linkedTrail ? { trail_id: linkedTrail.id.toString() } : {})
      });
    }
    setDropdownMenuState({ isOpen: true, options: dropdownOptions });
  };

  return (
    <FullscreenModal
      isOpen
      onCloseRequest={onClose}
      backgroundClassName={styles.modalBackground}
      closeButtonClassName={styles.closeButton}
      hidePageScrollbarGutter={hidePageScrollbarGutter}
    >
      <div className={styles.container}>
        {!isLoading && (
          <LightboxHeader
            user={currentPhoto ? photoUser(currentPhoto) : undefined}
            timestamp={currentPhoto?.timestamp}
            linkedTrail={linkedTrail}
            creditTitle={currentPhoto?.creditTitle}
            languageRegionCode={languageRegionCode}
            onClose={onClose}
            onDropdownMenuClick={context && onDropdownMenuClick}
            onUserClick={onUserClick}
            dropdownMenu={context && dropdownMenuState}
            setDropdownMenuOptions={setDropdownMenuState}
            dropdownOptions={dropdownOptions}
            shouldHideActions={shouldHideActions}
            photosPageLinkInfo={photosPageLinkInfo}
            onPhotosPageLinkClick={onPhotosPageLinkClick}
            showTimestampOnly={showTimestampOnly}
          />
        )}
        <div className={styles.navigationButtonLeft}>
          <IconButton
            className={activeIndex === 0 ? styles.hidden : ''}
            ref={lightboxNavigationButtonRef}
            loading={isLoading}
            icon={{ Component: ArrowSq, orientation: 'left' }}
            onClick={navigatePrev}
            testId="lightbox-prev"
            title={intl.formatMessage({ defaultMessage: 'Previous' })}
            variant="transparent"
          />
        </div>
        <DenaliLightboxPhoto url={currentPhoto?.url} title={currentPhoto && photoAlt(intl, currentPhoto)} isLoading={isLoading} />
        <div className={styles.navigationButtonRight}>
          <IconButton
            className={activeIndex === images.length - 1 ? styles.hidden : ''}
            ref={lightboxNavigationButtonRef}
            loading={isLoading}
            icon={{ Component: ArrowSq, orientation: 'right' }}
            onClick={navigateNext}
            testId="lightbox-next"
            title={intl.formatMessage({ defaultMessage: 'Next' })}
            variant="transparent"
          />
        </div>
        <LightboxFooter
          photoTitle={updatedPhotoTitle}
          attribution={currentPhoto?.creditTitle?.attribution}
          onDropdownMenuClick={context && onDropdownMenuClick}
          dropdownMenu={context && dropdownMenuState}
          setDropdownMenuOptions={setDropdownMenuState}
          dropdownOptions={dropdownOptions}
          shouldHideActions={shouldHideActions}
        />
        <RenamePhotoModal
          isOpen={isRenamePhotoModalOpen}
          name={updatedPhotoTitle}
          closeModal={() => setIsRenamePhotoModalOpen(false)}
          onRenamePhotoSave={onRenamePhotoSave}
          onRenamePhotoChange={onRenamePhotoChange}
        />
        {isDeletePhotoModalOpen && (
          <DeletePhotoAlertDialog onDeleteAlertClose={() => setIsDeletePhotoModalOpen(false)} onPhotoDeleteHandle={handleDeletePhoto} />
        )}

        <Toast
          isOpen={successToast.isVisible}
          message={successToast.text}
          position="alternative"
          type="success"
          testId="success-toast"
          icon={{ Component: CheckOutline }}
        />
        <Toast
          isOpen={errorToastVisible}
          message={intl.formatMessage({ defaultMessage: 'Something went wrong. Please try again.' })}
          position="alternative"
          type="error"
          testId="error-toast"
          icon={{ Component: Error }}
        />
        {context && context.currentUser && currentPhoto?.photo && setReportingSuccessToast && (
          <ReportIssueModal
            currentUserId={context.currentUser.id}
            entryPoint={EntryPoint.Photos}
            isOpen={isReportingModalOpen}
            reportId={String(currentPhoto.photo.id)}
            reportType="issue"
            reportUser={photoUser(currentPhoto)}
            onCloseRequest={() => setIsReportingModalOpen(false)}
            setSuccessToast={setReportingSuccessToast}
            analyticsData={analyticsReportLocation ? { location: analyticsReportLocation, contentType: 'photo' } : undefined}
          />
        )}
        <Toast
          isOpen={reportingSuccessToast.isVisible}
          message={reportingSuccessToast.text}
          position="alternative"
          type="success"
          testId="report-success-toast"
          icon={{ Component: Check }}
        />
      </div>
    </FullscreenModal>
  );
};

export default LightboxModal;
